// OSRM Routing utility for road-accurate routes
const OSRM_API = 'https://router.project-osrm.org/route/v1/driving';

/**
 * Get road-accurate route between waypoints using OSRM
 * @param {Array} waypoints - Array of [lat, lng] coordinates
 * @returns {Promise<Array>} - Array of [lat, lng] points following roads
 */
export const getRouteFromOSRM = async (waypoints) => {
  if (!waypoints || waypoints.length < 2) {
    return waypoints || [];
  }

  try {
    // Format coordinates for OSRM (lng,lat format)
    const coordinates = waypoints
      .map(point => `${point[1]},${point[0]}`)
      .join(';');

    const response = await fetch(
      `${OSRM_API}/${coordinates}?overview=full&geometries=geojson`
    );

    if (!response.ok) {
      console.warn('OSRM API error, falling back to straight lines');
      return waypoints;
    }

    const data = await response.json();

    if (data.routes && data.routes.length > 0) {
      // Convert GeoJSON coordinates to [lat, lng] format
      const routeCoords = data.routes[0].geometry.coordinates.map(
        coord => [coord[1], coord[0]]
      );
      return routeCoords;
    }

    return waypoints;
  } catch (error) {
    console.warn('Error fetching OSRM route:', error);
    return waypoints;
  }
};

/**
 * Animate marker movement smoothly
 * @param {L.Marker} marker - Leaflet marker to animate
 * @param {Array} from - Start [lat, lng]
 * @param {Array} to - End [lat, lng]
 * @param {number} duration - Animation duration in ms
 * @param {Function} onComplete - Callback when complete
 */
export const animateMarker = (marker, from, to, duration = 1000, onComplete) => {
  const startTime = Date.now();
  const deltaLat = to[0] - from[0];
  const deltaLng = to[1] - from[1];

  const animate = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    // Ease-out function for smooth deceleration
    const easeOut = 1 - Math.pow(1 - progress, 3);

    const currentLat = from[0] + deltaLat * easeOut;
    const currentLng = from[1] + deltaLng * easeOut;

    marker.setLatLng([currentLat, currentLng]);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (onComplete) {
      onComplete();
    }
  };

  requestAnimationFrame(animate);
};

/**
 * Calculate bearing/heading between two points
 * @param {Array} from - Start [lat, lng]
 * @param {Array} to - End [lat, lng]
 * @returns {number} - Bearing in degrees
 */
export const calculateBearing = (from, to) => {
  const lat1 = from[0] * Math.PI / 180;
  const lat2 = to[0] * Math.PI / 180;
  const dLng = (to[1] - from[1]) * Math.PI / 180;

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) -
            Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  let bearing = Math.atan2(y, x) * 180 / Math.PI;
  bearing = (bearing + 360) % 360;

  return bearing;
};

/**
 * Interpolate points along a route for smooth animation
 * @param {Array} route - Array of [lat, lng] coordinates
 * @param {number} numPoints - Number of interpolated points
 * @returns {Array} - Interpolated points
 */
export const interpolateRoute = (route, numPoints = 100) => {
  if (!route || route.length < 2) return route;

  const result = [];
  const totalDistance = calculateTotalDistance(route);
  const segmentDistance = totalDistance / numPoints;
  let currentDistance = 0;
  let currentIndex = 0;

  result.push(route[0]);

  for (let i = 1; i <= numPoints; i++) {
    const targetDistance = i * segmentDistance;

    while (currentIndex < route.length - 1) {
      const segDist = haversineDistance(route[currentIndex], route[currentIndex + 1]);

      if (currentDistance + segDist >= targetDistance) {
        const ratio = (targetDistance - currentDistance) / segDist;
        const lat = route[currentIndex][0] + ratio * (route[currentIndex + 1][0] - route[currentIndex][0]);
        const lng = route[currentIndex][1] + ratio * (route[currentIndex + 1][1] - route[currentIndex][1]);
        result.push([lat, lng]);
        break;
      }

      currentDistance += segDist;
      currentIndex++;
    }
  }

  result.push(route[route.length - 1]);
  return result;
};

const haversineDistance = (point1, point2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (point2[0] - point1[0]) * Math.PI / 180;
  const dLng = (point2[1] - point1[1]) * Math.PI / 180;
  const lat1 = point1[0] * Math.PI / 180;
  const lat2 = point2[0] * Math.PI / 180;

  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

const calculateTotalDistance = (route) => {
  let total = 0;
  for (let i = 0; i < route.length - 1; i++) {
    total += haversineDistance(route[i], route[i + 1]);
  }
  return total;
};

export default {
  getRouteFromOSRM,
  animateMarker,
  calculateBearing,
  interpolateRoute
};
